import { instanceToPlain } from 'class-transformer';
import sanitizePotentialString from '../utils/sanitizePotentialString';
import sanitizePotentialNumber from '../utils/sanitizePotentialNumber';
import Address, { AddressObject } from './Address';
import getStageFromTorontoPermit from '../helpers/buildingPermits/getStageFromTorontoPermit';
import { BuildingPermitStage } from './BuildingPermitStage';

export enum BuildingPermitSource {
  Toronto = 'toronto',
}

export enum BuildingPermitType {
  NewHouses = 'New Houses',
  NewBuilding = 'New Building',
  ResidentialBuildingPermit = 'Residential Building Permit', // NOTE: this is the old version of 'New Houses' and 'New Building'
  SmallResidentialProjects = 'Small Residential Projects',
}

export type BuildingPermitObject = {
  source: BuildingPermitSource;
  sourceId?: string;
  permitNum: string;
  revisionNum: string;
  permitType: BuildingPermitType;
  structureType: string;
  work: string;
  address: AddressObject;
  wardGrid: string;
  applicationDate: string;
  issuedDate: string | null;
  completedDate: string | null;
  status: string;
  stage: BuildingPermitStage;
  description: string | null;
  currentUse: string | null;
  proposedUse: string | null;
  dwellingUnitsCreated: number | null;
  dwellingUnitsLost: number | null;
  estConstCost: number | null;
  locationId?: string;
  latitude?: number;
  longitude?: number;
};

export type BuildingPermitRecord = {
  source: BuildingPermitSource;
  sourceId?: string;
  permitNum: string;
  revisionNum: string;
  permitType: BuildingPermitType;
  structureType: string;
  work: string;
  streetNum: string;
  streetName: string;
  streetType: string;
  streetDirection: string;
  postal: string;
  wardGrid: string;
  applicationDate: string;
  issuedDate: string | null;
  completedDate: string | null;
  status: string;
  stage: BuildingPermitStage;
  description: string | null;
  currentUse: string | null;
  proposedUse: string | null;
  dwellingUnitsCreated: number | null;
  dwellingUnitsLost: number | null;
  estConstCost: number | null;
  locationId?: string;
  point?: string;
  latitude?: number;
  longitude?: number;
};

export class BuildingPermit {
  source: BuildingPermitSource;
  sourceId?: string;
  permitNum: string;
  revisionNum: string;
  permitType: BuildingPermitType;
  structureType: string;
  work: string;
  address: Address;
  wardGrid: string;
  applicationDate: string;
  issuedDate: string | null;
  completedDate: string | null;
  status: string;
  stage: BuildingPermitStage;
  description: string | null;
  currentUse: string | null;
  proposedUse: string | null;
  dwellingUnitsCreated: number | null;
  dwellingUnitsLost: number | null;
  estConstCost: number | null;
  locationId?: string;
  latitude?: number;
  longitude?: number;

  static fromObject(buildingPermitObject: BuildingPermitObject) {
    return new BuildingPermit(buildingPermitObject);
  }

  static fromRecord(buildingPermitRecord: BuildingPermitRecord) {
    return new BuildingPermit({
      source: buildingPermitRecord.source,
      sourceId: buildingPermitRecord.sourceId,
      permitNum: buildingPermitRecord.permitNum,
      revisionNum: buildingPermitRecord.revisionNum,
      permitType: buildingPermitRecord.permitType,
      structureType: buildingPermitRecord.structureType,
      work: buildingPermitRecord.work,
      address: {
        streetNumber: buildingPermitRecord.streetNum,
        unitNumber: null,
        streetName: buildingPermitRecord.streetName,
        streetSuffix: buildingPermitRecord.streetType,
        streetDirection: buildingPermitRecord.streetDirection,
        zip: '',
        postalFsa: buildingPermitRecord.postal,
        city:
          buildingPermitRecord.source === BuildingPermitSource.Toronto
            ? 'Toronto'
            : '',
        area: '',
        state:
          buildingPermitRecord.source === BuildingPermitSource.Toronto
            ? 'Ontario'
            : '',
        neighborhood: '',
        majorIntersection: '',
        communityCode: '',
        district: '',
        country: '',
      },
      wardGrid: buildingPermitRecord.wardGrid,
      applicationDate: buildingPermitRecord.applicationDate,
      issuedDate: buildingPermitRecord.issuedDate,
      completedDate: buildingPermitRecord.completedDate,
      status: buildingPermitRecord.status,
      stage: buildingPermitRecord.stage,
      description: buildingPermitRecord.description,
      currentUse: buildingPermitRecord.currentUse,
      proposedUse: buildingPermitRecord.proposedUse,
      dwellingUnitsCreated: buildingPermitRecord.dwellingUnitsCreated,
      dwellingUnitsLost: buildingPermitRecord.dwellingUnitsLost,
      estConstCost: buildingPermitRecord.estConstCost,
      locationId: buildingPermitRecord.locationId,
      latitude: buildingPermitRecord.latitude,
      longitude: buildingPermitRecord.longitude,
    });
  }

  static fromTorontoBuildingPermitObject(torontoBuildingPermitObject: object) {
    return new BuildingPermit({
      source: BuildingPermitSource.Toronto,
      sourceId: torontoBuildingPermitObject['_id'].toString(),
      permitNum: torontoBuildingPermitObject['PERMIT_NUM'],
      revisionNum: torontoBuildingPermitObject['REVISION_NUM'],
      permitType: torontoBuildingPermitObject['PERMIT_TYPE'],
      structureType: torontoBuildingPermitObject['STRUCTURE_TYPE'],
      work: torontoBuildingPermitObject['WORK'],
      address: {
        streetNumber: torontoBuildingPermitObject['STREET_NUM'],
        unitNumber: null,
        streetName: torontoBuildingPermitObject['STREET_NAME'],
        streetSuffix:
          sanitizePotentialString(torontoBuildingPermitObject['STREET_TYPE']) ||
          '',
        streetDirection:
          sanitizePotentialString(
            torontoBuildingPermitObject['STREET_DIRECTION'],
          ) || '',
        zip: '',
        postalFsa: sanitizePotentialString(
          torontoBuildingPermitObject['POSTAL'],
        ),
        city: 'Toronto',
        area: '',
        state: 'Ontario',
        neighborhood: '',
        majorIntersection: '',
        communityCode: '',
        district: '',
        country: '',
      },
      wardGrid: torontoBuildingPermitObject['WARD_GRID'],
      applicationDate: torontoBuildingPermitObject['APPLICATION_DATE'],
      issuedDate: sanitizePotentialString(
        torontoBuildingPermitObject['ISSUED_DATE'],
      ),
      completedDate: sanitizePotentialString(
        torontoBuildingPermitObject['COMPLETED_DATE'],
      ),
      status: torontoBuildingPermitObject['STATUS'],
      stage: getStageFromTorontoPermit(torontoBuildingPermitObject),
      description: sanitizePotentialString(
        torontoBuildingPermitObject['DESCRIPTION'],
      ),
      currentUse: sanitizePotentialString(
        torontoBuildingPermitObject['CURRENT_USE'],
      ),
      proposedUse: sanitizePotentialString(
        torontoBuildingPermitObject['PROPOSED_USE'],
      ),
      dwellingUnitsCreated: sanitizePotentialNumber(
        torontoBuildingPermitObject['DWELLING_UNITS_CREATED'],
      ),
      dwellingUnitsLost: sanitizePotentialNumber(
        torontoBuildingPermitObject['DWELLING_UNITS_LOST'],
      ),
      estConstCost: sanitizePotentialNumber(
        torontoBuildingPermitObject['EST_CONST_COST'],
      ),
    });
  }

  constructor(buildPermitObject: BuildingPermitObject) {
    this.source = buildPermitObject.source;
    this.sourceId = buildPermitObject.sourceId;
    this.permitNum = buildPermitObject.permitNum;
    this.revisionNum = buildPermitObject.revisionNum;
    this.permitType = buildPermitObject.permitType;
    this.structureType = buildPermitObject.structureType;
    this.work = buildPermitObject.work;
    this.address = Address.fromObject(buildPermitObject.address);
    this.wardGrid = buildPermitObject.wardGrid;
    this.applicationDate = buildPermitObject.applicationDate;
    this.issuedDate = buildPermitObject.issuedDate;
    this.completedDate = buildPermitObject.completedDate;
    this.status = buildPermitObject.status;
    this.stage = buildPermitObject.stage;
    this.description = buildPermitObject.description;
    this.currentUse = buildPermitObject.currentUse;
    this.proposedUse = buildPermitObject.proposedUse;
    this.dwellingUnitsCreated = buildPermitObject.dwellingUnitsCreated;
    this.dwellingUnitsLost = buildPermitObject.dwellingUnitsLost;
    this.estConstCost = buildPermitObject.estConstCost;
    this.locationId = buildPermitObject.locationId;
    this.latitude = buildPermitObject.latitude;
    this.longitude = buildPermitObject.longitude;
  }

  structureWorkAndStageText() {
    return `${this.structureType}, ${this.work} (${this.stage})`;
  }

  isActive(): boolean {
    return [BuildingPermitStage.Applied, BuildingPermitStage.Issued].includes(
      this.stage,
    );
  }

  toRecord(): BuildingPermitRecord {
    return {
      source: this.source,
      sourceId: this.sourceId,
      permitNum: this.permitNum,
      revisionNum: this.revisionNum,
      permitType: this.permitType,
      structureType: this.structureType,
      work: this.work,
      streetName: this.address.streetName,
      streetNum: this.address.streetNumber,
      streetType: this.address.streetSuffix || null,
      streetDirection: this.address.streetDirection || null,
      postal: this.address.postalFsa || null,
      wardGrid: this.wardGrid,
      applicationDate: this.applicationDate,
      issuedDate: this.issuedDate,
      completedDate: this.completedDate,
      status: this.status,
      stage: this.stage,
      description: this.description,
      currentUse: this.currentUse,
      proposedUse: this.proposedUse,
      dwellingUnitsCreated: this.dwellingUnitsCreated,
      dwellingUnitsLost: this.dwellingUnitsLost,
      estConstCost: this.estConstCost,
      locationId: this.locationId,
      point:
        this.latitude && this.longitude
          ? `SRID=4326;POINT(${this.longitude} ${this.latitude})`
          : undefined,
      latitude: this.latitude,
      longitude: this.longitude,
    } as BuildingPermitRecord;
  }

  toObject(): BuildingPermitObject {
    return {
      ...instanceToPlain(this),
      address: this.address.toObject(),
    } as BuildingPermitObject;
  }
}
